import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ChatCircle, Users, VideoCamera, SignOut } from "@phosphor-icons/react";

const BottomTab = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    className={({ isActive }) =>
      `flex-1 flex flex-col items-center justify-center py-2 gap-1 border-t-2 transition-colors ${
        isActive ? "border-ink text-ink bg-paper-tint font-semibold" : "border-transparent text-ink-2 hover:text-ink"
      }`
    }
  >
    <Icon size={22} weight="duotone" />
    <span className="text-[11px] uppercase tracking-widest">{label}</span>
  </NavLink>
);

const SubTab = ({ to, label, testid, end = true }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className={({ isActive }) =>
      `px-4 py-2 text-xs uppercase tracking-widest border ${
        isActive ? "bg-ink text-white border-ink" : "bg-paper-white text-ink border-line hover:bg-paper-tint"
      }`
    }
  >
    {label}
  </NavLink>
);

const SectionTabs = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/app/messages")) return null; // chat is fullscreen
  if (pathname.startsWith("/app/meet/room/")) return null; // meeting room is fullscreen
  if (pathname.startsWith("/app/network") || pathname.startsWith("/app/people") || pathname.startsWith("/app/profile")) {
    return (
      <div className="border-b border-line bg-paper-white px-4 py-3 flex gap-2 flex-wrap" data-testid="network-subnav">
        <SubTab to="/app/network" label="Feed" testid="subnav-feed" />
        <SubTab to="/app/people" label="People" testid="subnav-people" />
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMessages = pathname.startsWith("/app/messages");
  const isMeetRoom = pathname.startsWith("/app/meet/room/");
  const isFullBleed = isMessages || isMeetRoom;

  return (
    <div className="h-screen flex flex-col bg-paper">
      {/* Top bar */}
      <header className="border-b border-line bg-paper-white shrink-0 z-30">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_omni-platform-20/artifacts/o5vuayu0_handy%20buzz%20%281%29.png"
              alt="Handy Buzzer"
              className="w-8 h-8 object-contain mix-blend-multiply"
            />
            <span className="font-heading font-black text-lg tracking-tight">HANDY BUZZER</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/app/profile/${user?.user_id}`)}
              className="flex items-center gap-2 px-2 py-1 brut-hover"
              data-testid="profile-btn"
            >
              <img
                src={user?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${user?.email}`}
                className="w-7 h-7 border border-line object-cover"
                alt=""
              />
              <span className="text-xs font-semibold hidden sm:inline">{user?.name?.split(" ")[0]}</span>
            </button>
            <button onClick={logout} className="p-2 text-ink-2 hover:text-ink" data-testid="logout-btn" title="Sign out">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Section sub-nav */}
      <div className="shrink-0">
        <SectionTabs />
      </div>

      {/* Content */}
      <main className={`flex-1 min-h-0 ${isFullBleed ? "overflow-hidden" : "overflow-y-auto"}`}>
        <Outlet />
      </main>

      {/* Bottom navigation */}
      <nav className="bg-paper-white border-t border-line shrink-0 flex" data-testid="bottom-nav">
        <BottomTab to="/app/messages" icon={ChatCircle} label="Messages" testid="tab-messages" />
        <BottomTab to="/app/network" icon={Users} label="Network" testid="tab-network" />
        <BottomTab to="/app/meet" icon={VideoCamera} label="Meetings" testid="tab-meet" />
      </nav>
    </div>
  );
};

export default Dashboard;
